import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { DEL_SUPPLIER_PAYOUT } from "apollo/mutuations";
import {
  GET_SUPPLIERS_ORDERS_REPORT,
  GET_SUPPLIERS_PAYOUTS,
} from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import SupplierAddPayout from "components/payout/addPayout";
import B2bTable from "components/reusable/b2bTable";
import ReportFilters from "components/reusable/ReportFilters";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";
import { Check } from "tabler-icons-react";
import { formatNumber } from "utiles/url";
import SupplierOrdersTable from "./orders";
const SupplierPayout = ({ isSme = false }) => {
  const [size, setSize] = useState("10");
  const [openedDelete, setOpenedDelete] = useState(false);
  const [openedDetail, setOpenedDetail] = useState(false);
  const [detailUserName, setDetailUserName] = useState(null);
  const [detailTotalAmount, setDetailTotalAmount] = useState(null);
  const [user] = useState(null);
  const [editId, setEditId] = useState();
  const [opened, setOpened] = useState(false);
  const [deleteID, setDeleteID] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);

  const { data, loading, refetch } = useQuery(GET_SUPPLIERS_ORDERS_REPORT, {
    variables: {
      first: Number.parseInt(size, 10),
      page: activePage,
      is_sme: isSme,
      // ordered_by: [
      //   {
      //     column: "CREATED_AT",
      //     order: "DESC",
      //   },
      // ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to Get Suppliers Orders",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };
  useEffect(() => {
    if (data) {
      setTotal(data.getSuppliersOrdersReport.paginatorInfo.lastPage);
    }
  }, [data, size]);
  useEffect(() => {
    refetch();
  }, [refetch]);

  const [fetchAllSupplierOrders] = useLazyQuery(GET_SUPPLIERS_ORDERS_REPORT);

  const handleExportDataFetch = async () => {
    const { data: allData } = await fetchAllSupplierOrders({
      variables: {
        first: null,
        page: 1,
        is_sme: isSme,
      },
    });
    return allData?.getSuppliersOrdersReport?.data || [];
  };

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx =
          (activePage - 1) * Number.parseInt(size || "0", 10) + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "supplier.user.name",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.user.name}</span>;
      },
    },
    {
      label: "Phone",
      key: "supplier.user.phone",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.user.phone}</span>;
      },
    },
    {
      label: "Net Amount",
      key: "amount",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return <span>{formatNumber(rowData.total_net_amount)}</span>;
      },
    },
    {
      label: "Paid",
      key: "amount",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        const paid = rowData.total_paid_amount;
        return (
          <Badge variant="light" color={paid > 0 ? "green" : "red"}>
            {formatNumber(paid)}
          </Badge>
        );
      },
    },
    {
      label: "Unpaid",
      key: "amount",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return <span>{formatNumber(rowData.remaining_amount)}</span>;
      },
    },
    {
      label: "Status",
      key: "status",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <span>
            {rowData.remaining_amount === 0 ? (
              <Badge variant="light" color="green">
                Paid
              </Badge>
            ) : (
              <Badge variant="light" color="red">
                Partial paid
              </Badge>
            )}
          </span>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      exportable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <div style={{ width: "150px", display: "flex", gap: "6px" }}>
            {rowData.remaining_amount > 0 && (
              <ActionButton
                label="Pay"
                color="green"
                onClick={() => {
                  setEditId(rowData);
                  setOpened(true);
                }}
              >
                <Check size={18} />
              </ActionButton>
            )}
            <ActionButton
              label="View"
              color="success"
              onClick={() => handleManageCategory(rowData)}
            >
              <FiEye fontSize="medium" />
            </ActionButton>
          </div>
        );
      },
    },
  ];
  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };
  const handleManageCategory = (item) => {
    setEditId(item.businesses_report);
    setDetailUserName(item.user?.name || null);
    setDetailTotalAmount(item.total_net_amount ?? null);
    setOpenedDetail(true);
  };

  const [confirmedSearch, setConfirmedSearch] = useState("");
  const clearInput = () => {
    setConfirmedSearch("");
  };

  const theme = useMantineTheme();

  const [delCategory] = useMutation(DEL_SUPPLIER_PAYOUT, {
    update(cache, { data: { deleteSupplierPayout } }) {
      cache.updateQuery(
        {
          query: GET_SUPPLIERS_PAYOUTS,
          variables: {
            supplier_id: user,
            first: Number.parseInt(size, 10),
            page: activePage,
            search: "",
            is_sme: false,
            ordered_by: [
              {
                column: "CREATED_AT",
                order: "DESC",
              },
            ],
          },
        },
        (data) => {
          if (data.supplierPayouts.data.length === 1) {
            setTotal(total - 1);
            setActivePage(activePage - 1);
          } else {
            return {
              supplierPayouts: {
                data: [
                  ...data.supplierPayouts.data.filter(
                    (category) => category.id !== deleteSupplierPayout.id,
                  ),
                ],
              },
            };
          }
        },
      );
    },
  });

  const deleteBusines = () => {
    delCategory({
      variables: { id: deleteID },
      onCompleted(data) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "Business Deleted Successfully",
        });
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to Delete Payout",
        });
      },
    });
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <ReportFilters
        data={data ? data.getSuppliersOrdersReport.data : []}
        headers={headerData}
        reportTitle={isSme ? "SME Supplier Orders Report" : "Supplier Orders Report"}
        filterSubtitle={isSme ? "All SME Supplier Orders" : "All Suppliers Orders"}
        fileNamePrefix={isSme ? "sme_supplier_orders_report" : "supplier_orders_report"}
        onFetch={handleExportDataFetch}
      />
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        title="Addding a Payout"
        padding="xl"
        size="80%"
        position="bottom"
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
      >
        <SupplierAddPayout
          size={size}
          activePage={activePage}
          supplierPayout={editId}
          setOpened={setOpened}
          refetch={refetch}
        />
      </Drawer>
      <Drawer
        opened={openedDetail}
        onClose={() => setOpenedDetail(false)}
        title="Supplier Orders"
        padding="xl"
        size="80%"
        position="bottom"
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
      >
        <SupplierOrdersTable
          ordersList={editId}
          userName={detailUserName}
          totalAmount={detailTotalAmount}
          setOpenedDetail={setOpenedDetail}
        />
      </Drawer>
      <Modal
        opened={openedDelete}
        onClose={() => setOpenedDelete(false)}
        title="Warning"
        centered
      >
        <p>Are you sure do you want todelete this supplier payout?</p>
        <Group position="right">
          <Button onClick={() => deleteBusines()} color="red">
            Delete
          </Button>
        </Group>
      </Modal>
      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            searchInput={false}
            clearInput={clearInput}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            loading={loading}
            layout={{ minWidth: 700 }}
            data={data ? data.getSuppliersOrdersReport.data : []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default SupplierPayout;
